<template>
  <div class="max-w-screen">
    <SecondaryBar v-if="!isApplicableCollegeCoach" />
    <AthleteIdentifyingBar id="athlete-identifying-bar" v-bind="$attrs" @close="close" />
    <AthleteSecondaryInfo />
    <AthleteProfileShowcase :user-id="userId" :show-navbar="showNavbar" />


    <Portal to="navigation-bar">
      <div id="navigation-bar" class="px-3 md:px-6 bg-white drop-shadow">
        <div id="navigation-bar" class="flex justify-start space-x-2 max-w-1140 w-full mx-auto">
          <a
            v-for="section in sections"
            :key="section.id"
            :href="'#' + section.id"
            :class="{
              'text-blue-300 border-blue-300 border-b-4 active': currentSection === section.id,
            }"
            :data-test-id="section.id"
            class="px-1 pt-4 pb-3 ds-text-base font-medium text-gray-500 border-solid border-0 border-transparent transition-colors ease-in-out duration-200"
            @click.prevent="scrollToSection(section.id)"
          >
            {{ section.name }}
          </a>
        </div>
      </div>
    </Portal>

    <!-- Athlete Stats and Information -->
    <div class="lg:max-w-6xl mx-auto px-3 sm:px-4 md:px-6" @scroll="onScroll">
      <AthleteVideos id="video" :user-id="userId" />
      <AthleteContactInformation id="contact" :show-message-button="showMessageButton" />
      <AthleteAthletic id="athletic" />
      <AthleteEvents :user-id="userId" />
      <AthleteAcademic id="academic" />
    </div>
  </div>
</template>

<script>
import AthleteVideos from "../ProfileSections/Video/Library.vue";
import AthleteContactInformation from "../ProfileSections/Contact/AthleteContactInformation.vue";
import AthleteAthletic from "../ProfileSections/Athletic/AthleteAthletic.vue";
import AthleteEvents from "../ProfileSections/Events/AthleteEvents.vue";
import AthleteAcademic from "../ProfileSections/Academic/AthleteAcademic.vue";
import SecondaryBar from "../SecondaryBar/index.vue";
import { mapGetters, mapState } from "vuex";
import AthleteIdentifyingBar from './AthleteIdentifyingBar.vue';
import AthleteSecondaryInfo from "./AthleteSecondaryInfo.vue";
import AthleteProfileShowcase from "./AthleteProfileShowcase/AthleteProfileShowcase.vue";

export default {
  components: {
    AthleteSecondaryInfo,
    AthleteIdentifyingBar,
    AthleteVideos,
    AthleteContactInformation,
    AthleteAthletic,
    AthleteEvents,
    AthleteAcademic,
    AthleteProfileShowcase,
    SecondaryBar,
  },
  props: {
    userId: { required: true },
    showMessageButton: { type: Boolean, default: true },
    showNavbar: { type: Boolean, default: false },
  },
  data() {
    return {
      sections: [
        { id: 'video', name: 'Video' },
        { id: 'contact', name: 'Contact' },
        { id: 'athletic', name: 'Athletic' },
        { id: 'academic', name: 'Academic' },
      ],
      currentSection: 'video',
      scrollParent: null,
      renderedSections: [],
    };
  },
  computed: {
    ...mapGetters("userData", ["isLoggedOut"]),
    ...mapGetters("athleteProfile", ["canSendFeedbackRequest", "isApplicableCollegeCoach"]),
    ...mapState('athleteProfile', ['isModal', 'showSecondaryNavbar']),
    isMobile() {
      return ["xs", "sm", "mobile"].includes(this.$mq);
    },
    sectionYOffset() {
      if (this.isLoggedOut || this.isModal || !this.showSecondaryNavbar) {
        return this.isMobile ? 120 : 130;
      }
      return this.isMobile ? 190 : 200;
    },
  },
  async mounted() {
    await this.$store.dispatch("athleteProfile/fetchAthlete", this.userId);
    await this.$store.dispatch("videoLibrary/fetchVideoPlaylist", this.userId);
    if (!this.isLoggedOut) {
      await this.$store.dispatch("videoLibrary/fetchUserHasUploadedVideo", this.userId);
    }
    this.$emit("loaded");
    this.scrollParent = await this.findScrollParent(this.$el);
    this.scrollParent.addEventListener('scroll', this.onScroll);
  },
  beforeDestroy() {
    if (this.scrollParent) {
      this.scrollParent.removeEventListener('scroll', this.onScroll);
    }
  },
  methods: {
    findScrollParent(element) {
      return new Promise(resolve => {
        this.$nextTick(() => {
          let parent = element.parentNode;
          while (parent && parent !== document) {
            const overflowY = window.getComputedStyle(parent).overflowY;
            const hasVerticalScrollbar = overflowY === 'auto' || overflowY === 'scroll';

            if (hasVerticalScrollbar) {
              const hasOverflow = parent.scrollHeight > parent.clientHeight ||
                parent.offsetHeight > parent.clientHeight;

              if (hasOverflow) {
                resolve(parent);
                return;
              }
            }
            parent = parent.parentNode;
          }
          resolve(window);
        });
      });
    },
    onScroll() {
      this.currentSection = this.calculateActiveSection();
    },
    calculateActiveSection() {
      const scrollParent = this.scrollParent === window ? document.documentElement : this.scrollParent;

      // Adding one because on IPad Pro it stops at .99 pixels before the end
      const reachedEnd = scrollParent.scrollTop + scrollParent.clientHeight + 1 >= scrollParent.scrollHeight;
      if (reachedEnd) {
        return this.sections[this.sections.length - 1].id;
      }

      let currentSection = this.currentSection
      for (const section of this.sections) {
        const element = document.getElementById(section.id);
        if (!element) continue;

        const rect = element.getBoundingClientRect();
        if (rect.top <= this.sectionYOffset + 5 && rect.bottom >= this.sectionYOffset + 5) {
          currentSection = section.id;
        }
      }
      return currentSection;
    },
    scrollToSection(sectionId) {
      const sectionElement = document.getElementById(sectionId);
      if (!sectionElement) return;

      const scrollPosition = this.calculateScrollPosition(sectionElement);
      this.scrollParent.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    },
    calculateScrollPosition(sectionElement) {
      const sectionElementRect = sectionElement.getBoundingClientRect();

      let parentOffset = 0;
      if (this.scrollParent === window) {
        parentOffset = window.pageYOffset;
      } else {
        const scrollParentRect = this.scrollParent.getBoundingClientRect();
        parentOffset = this.scrollParent.scrollTop - scrollParentRect.top;
      }

      return sectionElementRect.top + parentOffset - this.sectionYOffset;
    },
    close() {
      this.$emit("close");
    },
  },
};
</script>

<style>
p {
  margin: 0;
}

.drop-shadow {
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
}
</style>